import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Phone, CalendarClock, Check, AlertTriangle, RotateCcw } from "lucide-react";

// A due list with contact tracking. Deliberately does NOT send anything —
// automated SMS drags in telco integration and patient consent, and a worklist
// reception can actually work through is the useful 90%.
const FILTERS = [
  { key: "due", label: "Due & overdue" },
  { key: "open", label: "All open" },
  { key: "scheduled", label: "Scheduled" },
  { key: "completed", label: "Completed" },
];

export default function DentalRecalls() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("due");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async (f = filter) => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const params = f === "due"
        ? `status=open&due_before=${today}`
        : f === "open" ? "status=open"
        : `status=${f}`;
      const { data } = await api.get(`/dental/recalls?${params}`);
      setRows(data || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(filter); /* eslint-disable-next-line */ }, [filter]);

  const overdueCount = useMemo(() => rows.filter((r) => r.is_overdue).length, [rows]);

  const act = async (id, fn) => {
    setBusy(id);
    try { await fn(); await load(); }
    catch (e) { toast.error(formatErr(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const markContacted = (r) => act(r.id, async () => {
    await api.post(`/dental/recalls/${r.id}/contacted`, { channel: "phone" });
    toast.success("Contact logged");
  });

  const markScheduled = (r) => act(r.id, async () => {
    await api.put(`/dental/recalls/${r.id}`, { status: "scheduled" });
    toast.success("Marked as scheduled");
  });

  const markDone = (r) => act(r.id, async () => {
    await api.put(`/dental/recalls/${r.id}`, { status: "completed" });
    toast.success("Recall closed");
  });

  const reopen = (r) => act(r.id, async () => {
    await api.put(`/dental/recalls/${r.id}`, { status: "open" });
  });

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="dental-recalls">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Work Sans" }}>
          Recalls
        </h1>
        <p className="text-sm text-slate-500">
          Patients due back for cleaning, maintenance or review.
          {overdueCount ? <span className="text-red-600 font-medium"> {overdueCount} overdue.</span> : null}
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={
              "px-3 py-1.5 rounded-full text-sm border transition " +
              (filter === f.key
                ? "bg-blue-600 border-blue-600 text-white"
                : "bg-white border-slate-300 text-slate-600 hover:border-slate-400")
            }
            data-testid={`recall-filter-${f.key}`}
          >
            {f.label}
          </button>
        ))}
        <Button size="sm" variant="ghost" onClick={() => load()}>
          <RotateCcw size={14} className="mr-1" /> Refresh
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">Nothing here — nobody is due.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {rows.map((r) => (
              <div key={r.id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`recall-${r.id}`}>
                {r.is_overdue
                  ? <AlertTriangle size={16} className="text-red-600 shrink-0" />
                  : <CalendarClock size={16} className="text-slate-400 shrink-0" />}

                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {r.patient_snapshot?.full_name || "Patient"}
                    <span className="ml-2 text-xs font-normal text-slate-500">{r.type_label}</span>
                  </div>
                  <div className="text-[11px] text-slate-400">
                    Due {r.due_date}
                    {r.is_overdue ? " · overdue" : ""}
                    {r.patient_snapshot?.contact_number ? ` · ${r.patient_snapshot.contact_number}` : " · no contact number"}
                    {r.last_contacted_at ? ` · last contacted ${r.last_contacted_at.slice(0, 10)}` : ""}
                    {r.contact_attempts?.length ? ` (${r.contact_attempts.length}×)` : ""}
                  </div>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {r.patient_id ? (
                    <Button asChild size="sm" variant="ghost">
                      <Link to={`/dental/patients/${r.patient_id}/chart`}>Chart</Link>
                    </Button>
                  ) : null}
                  {r.status === "completed" ? (
                    <Button size="sm" variant="outline" onClick={() => reopen(r)} disabled={busy === r.id}>
                      Reopen
                    </Button>
                  ) : (
                    <>
                      <Button size="sm" variant="outline" onClick={() => markContacted(r)} disabled={busy === r.id}
                              data-testid={`btn-contacted-${r.id}`}>
                        <Phone size={13} className="mr-1" /> Contacted
                      </Button>
                      {r.status !== "scheduled" ? (
                        <Button size="sm" variant="outline" onClick={() => markScheduled(r)} disabled={busy === r.id}>
                          Booked
                        </Button>
                      ) : null}
                      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => markDone(r)} disabled={busy === r.id}>
                        <Check size={13} className="mr-1" /> Done
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <p className="text-xs text-slate-400">
        Recalls do not send messages. Use <b>Contacted</b> to record that you called or messaged
        the patient, so the next person does not chase them again.
      </p>
    </div>
  );
}
